import React, { useState } from 'react';
import Linkify from 'react-linkify';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import { CustomIcon, Paper, Translation } from '../../common';

import { bic, iban } from '../../../constants/regex';
import { onClickWrapper } from '../../../utils/ui';
import { connectTo, shrinkName } from '../../../utils';
import { getOneTimeToken } from '../../../utils/token';
import {
  getPureUrl,
  getSearchString,
  getSearchUrl,
  getUrlParams
} from '../../../utils/httpUtil';

import {
  CHANNEL_SMS,
  CUSTOMER_MESSAGE_TYPES_MAP,
  filterOptionsMap
} from '../../../constants/post-box';
import { useAppWidthClass } from '../../../utils/hooks';
import { enqueueSnackbar } from '../../../actions/snackbar';

export const maxTextSize = 150;

const MAX_DOC_SIZE_LG = 50;
const MAX_DOC_SIZE_TABLET = 28;
const MAX_DOC_SIZE_MOBILE = 20;

const INTERNAL_NOTE_KEY = 'UNIC_COMMUNICATION_INTERNALNOTE';
const CLIENT_EMAIL_KEY = 'UNIC_COMMUNICATION_CLIENT';

export const createLink = (url, authToken) => {
  let regexp = new RegExp(/\?/);
  return regexp.test(url)
    ? url + '&jwtToken=' + authToken
    : url + '?jwtToken=' + authToken;
};

export const convertUniCodes = text => {
  if (text && text.indexOf('&#') >= 0) {
    return text.replace(/&#(\w+);/g, (m, code) => String.fromCodePoint(code));
  }
  return text;
};

export const formatText = (text, isOpen, maxSize) => {
  const convertedText = convertUniCodes(text);
  if (convertedText.length > maxSize) {
    return isOpen
      ? convertedText
      : convertedText.slice(0, maxTextSize) + ' ...';
  } else {
    return convertedText;
  }
};

export const AddCopyIbanBic = ({ text, className, onCopy }) => {
  const copyRegex = new RegExp(iban.source + '[,\\n] BIC:? ' + bic.source, 'g');

  let match;
  const matches = [];
  while ((match = copyRegex.exec(text))) {
    matches.push({ index: match.index, value: match[0] });
  }

  if (matches.length) {
    let lastIndex = 0;
    const elements = [];
    matches.forEach(({ index, value }) => {
      if (index > lastIndex) {
        elements.push(
          <Linkify
            properties={{
              onClick: onClickWrapper(
                'page.postbox.component.notification.link'
              )
            }}
          >
            {text.substring(lastIndex, index)}
          </Linkify>
        );
      }

      elements.push(
        <CopyToClipboard
          onCopy={onClickWrapper(
            'page.postbox.component.notification.copy-to-clipboard',
            onCopy
          )}
          text={value}
        >
          <span className={className}>{value}</span>
        </CopyToClipboard>
      );
      lastIndex = index + value.length;
    });

    if (text.length > lastIndex) {
      elements.push(
        <Linkify
          properties={{
            onClick: onClickWrapper('page.postbox.component.notification.link')
          }}
        >
          {text.substring(lastIndex)}
        </Linkify>
      );
    }

    return elements.length === 1
      ? elements[0]
      : elements.map((el, idx) => ({
          ...el,
          key: idx
        }));
  } else {
    return (
      <Linkify
        properties={{
          onClick: onClickWrapper('page.postbox.component.notification.link')
        }}
      >
        {text}
      </Linkify>
    );
  }
};

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'relative',
    margin: '8px 0',
    padding: '0 12px',
    display: 'flex',
    justifyContent: 'flex-start'
  },
  wrapperCustomer: {
    justifyContent: 'flex-end'
  },
  notification: {
    maxWidth: '516px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.background.default,
    justifyContent: 'space-between',
    color: theme.palette.text.primary,
    border: `2px solid ${theme.palette.secondary.main}`,
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.25)',
    borderRadius: '16px',
    padding: '20px 16px 16px 12px',

    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: '20px',
      left: '-1px',
      transform: 'translateX(-50%) rotate(-45deg)',
      width: '16px',
      height: '16px',
      background: theme.palette.background.default,
      borderTop: `2px solid ${theme.palette.secondary.main}`,
      borderLeft: `2px solid ${theme.palette.secondary.main}`
    }
  },
  notificationMobile: {
    maxWidth: '258px'
  },
  notificationFromCustomer: {
    borderColor: theme.palette.primary.main,

    '&::before': {
      top: '20px',
      left: 'unset',
      right: '-1px',
      transform: 'translateX(50%) rotate(135deg)',
      width: '16px',
      height: '16px',
      borderTopColor: theme.palette.primary.main,
      borderLeftColor: theme.palette.primary.main
    }
  },
  notificationByLawyer: {
    borderColor: theme.palette.warning.dark,
    color: theme.palette.warning.dark,

    '&::before': {
      borderTopColor: theme.palette.warning.dark,
      borderLeftColor: theme.palette.warning.dark
    }
  },
  notificationByClient: {
    borderColor: theme.palette.primary.dark,

    '&::before': {
      borderTopColor: theme.palette.primary.dark,
      borderLeftColor: theme.palette.primary.dark
    }
  },
  notificationInternal: {
    borderColor: theme.palette.secondary.dark,
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.contrastText,

    '&::before': {
      background: theme.palette.secondary.dark
    }
  },
  notificationClient: {
    borderColor: theme.palette.secondary.light,
    background: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText,

    '&::before': {
      borderTopColor: theme.palette.secondary.light,
      borderLeftColor: theme.palette.secondary.light,
      background: theme.palette.secondary.light
    }
  },
  header: {
    minHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary
  },
  headerLink: {
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    height: 'auto',
    cursor: 'pointer',
    color: 'inherit',
    transition: 'all 100ms ease-in-out',

    '&:hover': {
      color: theme.palette.text.primary
    },
    '&:focus,&:active': {
      outline: 'none'
    }
  },
  headerIcon: { marginRight: '16px' },
  headerTitle: {
    ...theme.typography.subtitle2,
    textAlign: 'left',
    textDecoration: 'underline',
    margin: 0,
    color: 'inherit'
  },
  body: {
    ...theme.typography.body2,
    paddingTop: '16px',
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',

    '& a': {
      color: theme.palette.secondary.main
    }
  },
  internalContent: {
    color: theme.palette.secondary.contrastText,
    '& a': {
      color: theme.palette.grey['100']
    }
  },
  copy: {
    cursor: 'pointer',
    '&:hover': {
      fontWeight: 'bolder'
    }
  },
  showMore: {
    ...theme.typography.subtitle1,
    marginTop: '8px',
    padding: 0,
    border: 0,
    background: 'none',
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    textAlign: 'left',
    cursor: 'pointer',

    '&:focus,&:active': {
      outline: 'none'
    }
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    margin: '8px 0 0 0',
    padding: 0
  },
  file: {
    width: '100%',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    textAlign: 'left',
    padding: '8px',
    border: `1px solid ${theme.palette.text.disabled}`,
    borderRadius: '8px',
    background: theme.palette.background.default,
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    transition: 'all 100ms ease-in-out',

    '&:hover': {
      boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.25)',
      color: theme.palette.text.primary
    },
    '&:focus,&:active': {
      outline: 'none'
    }
  },
  fileIcon: {
    marginRight: '16px'
  },
  fileName: {
    ...theme.typography.subtitle2,
    paddingTop: '4px',
    color: 'inherit',
    wordBreak: 'break-word'
  }
}));

const ICON_MAP = {
  'email_icon.svg': 'mailIcon',
  'pdf_icon.svg': 'pdfIcon',
  'image_icon.svg': 'imageIcon',
  'documents_icon.png': 'wordIcon',
  'word_icon.svg': 'wordIcon'
};

const Notification = ({
  text,
  datetime,
  type,
  mainDoc,
  docs,
  subject,
  name,
  channel,
  translations
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isTabletWidth, isMobileWidth } = useAppWidthClass();

  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const onTextCopy = () =>
    dispatch(
      enqueueSnackbar({
        message: 'Copied!',
        options: {
          variant: 'success'
        }
      })
    );

  const onAttachmentClick = url => () => {
    getOneTimeToken().then(token => {
      if (token) {
        const pureUrl = getPureUrl(url);
        const query = getUrlParams(getSearchString(url));

        window.open(
          getSearchUrl({
            url: pureUrl,
            query: {
              ...query,
              oneTimeToken: token
            }
          }),
          '_blank'
        );
      }
    });
  };

  const isFromCustomer = type === CUSTOMER_MESSAGE_TYPES_MAP.FROM_CUSTOMER;
  const isByLawyer =
    type === CUSTOMER_MESSAGE_TYPES_MAP.TO_CUSTOMER_FROM_LAWYER;
  const isByClient =
    type === CUSTOMER_MESSAGE_TYPES_MAP.TO_CUSTOMER_FROM_CLIENT;
  const isInternalNote = type === CUSTOMER_MESSAGE_TYPES_MAP.INTERNAL;
  const isClient = type === CUSTOMER_MESSAGE_TYPES_MAP.CLIENT;

  const isInternal = isInternalNote || isClient;
  const headerKey = isInternalNote
    ? INTERNAL_NOTE_KEY
    : isClient
    ? CLIENT_EMAIL_KEY
    : '';

  const header =
    (isInternal
      ? (translations[headerKey] ? translations[headerKey] + ' - ' : '') +
        (name ? name + ': ' : '')
      : '') +
    (datetime + (subject && !isInternal ? ' | ' + subject : ''));

  const isSMS = channel === CHANNEL_SMS;

  return (
    <div
      className={classNames(
        classes.wrapper,
        isFromCustomer && classes.wrapperCustomer
      )}
      data-id="notification-wrapper"
      data-notification-channel={filterOptionsMap[channel] || channel}
    >
      <Paper
        className={classNames(
          classes.notification,
          isFromCustomer && classes.notificationFromCustomer,
          isByLawyer && classes.notificationByLawyer,
          isByClient && classes.notificationByClient,
          isInternalNote && classes.notificationInternal,
          isClient && classes.notificationClient,
          isMobileWidth && classes.notificationMobile
        )}
      >
        <div className={classes.header}>
          {mainDoc ? (
            <button
              className={classes.headerLink}
              onClick={onAttachmentClick(mainDoc.url)}
              title="download link"
            >
              <CustomIcon
                className={classes.headerIcon}
                size="small"
                icon={
                  ICON_MAP[mainDoc.fileName]
                    ? ICON_MAP[mainDoc.fileName]
                    : 'mailIcon'
                }
                variant={
                  isByLawyer
                    ? 'warningDark'
                    : isClient
                    ? 'secondaryContrast'
                    : 'primary'
                }
              />
              <h4
                className={classNames(
                  classes.headerTitle,
                  isInternal && classes.internalContent
                )}
              >
                {header}
              </h4>
            </button>
          ) : (
            <>
              {isSMS && (
                <CustomIcon
                  className={classes.headerIcon}
                  size="small"
                  icon="chatIcon"
                  variant={
                    isByLawyer
                      ? 'warningDark'
                      : isClient
                      ? 'secondaryContrast'
                      : 'primary'
                  }
                />
              )}
              <h4
                className={classNames(
                  classes.headerTitle,
                  isInternal && classes.internalContent
                )}
              >
                {header}
              </h4>
            </>
          )}
        </div>
        {text && (
          <>
            <div
              className={classNames(
                classes.body,
                isInternal && classes.internalContent
              )}
            >
              <AddCopyIbanBic
                text={formatText(text, isOpen, maxTextSize)}
                onCopy={onTextCopy}
                className={classes.copy}
              />
            </div>
            {convertUniCodes(text).length > maxTextSize && (
              <button
                id={`page.postbox.component.notification.show-${
                  isOpen ? 'less' : 'more'
                }`}
                onClick={handleClick}
                className={classNames(
                  classes.showMore,
                  isInternal && classes.internalContent
                )}
              >
                <Translation inline>
                  {isOpen ? 'SHOW_LESS' : 'SHOW_MORE'}
                </Translation>
              </button>
            )}
            {docs && (
              <ul className={classes.fileList}>
                {docs.map(({ url, name, fileName }) => (
                  <button
                    key={url}
                    className={classes.file}
                    onClick={onAttachmentClick(url)}
                  >
                    <CustomIcon
                      className={classes.fileIcon}
                      size="small"
                      icon={
                        ICON_MAP[fileName] ? ICON_MAP[fileName] : 'mailIcon'
                      }
                      variant="primary"
                    />
                    <div className={classes.fileName}>
                      {shrinkName(
                        name,
                        !isMobileWidth && !isTabletWidth
                          ? MAX_DOC_SIZE_LG
                          : isMobileWidth
                          ? MAX_DOC_SIZE_MOBILE
                          : MAX_DOC_SIZE_TABLET
                      )}
                    </div>
                  </button>
                ))}
              </ul>
            )}
          </>
        )}
      </Paper>
    </div>
  );
};

export default connectTo(
  ({ translations: { translations } }) => ({ translations }),
  {},
  Notification
);
